/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {isDeepEqual} from '@ion/core/src/utils/Equality';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import Layer from '../components/operationalLayers/Layer';
import {LayerNested} from '../components/operationalLayers/types';
import {
  LayerSettingsCurrentRowStyles,
  LayerSettingsProps,
  LayerSettingsStyles
} from './LayerSettings';

// defines the prop definitions for the OperationalLayers
export type OperationalLayersSettingsProps = LayerSettingsProps & {
  operationalLayers: Array<LayerNested>,
  onOpacityChange: Function,
  onToggleExpand?: (event: object, expanded: boolean) => void,
  onLayerVisibilityChange?: Function,
  onSublayerVisibilityChange?: Function,
  islayerVisibilityUpdate?: boolean,
  opacity?: Array<Object>,
  expandedLayer: string,
  layers?: string[]
};

// defines the styles used by the OperationalLayers
const useStyles = makeStyles(() => ({
  ...LayerSettingsStyles(),
  ...LayerSettingsCurrentRowStyles()
}));

/**
 * @OperationalLayersSettings
 *
 * The Operational Layers Settings provides a component which allows users to select
 * specific Operational Layers related attributes to be displayed within a DLSMap.
 *
 * ```typescript
 *
 *   <OperationalLayersSettings isEnabled={}
 *     onToggle={}
 *     onOpacityChange={}
 *     opacity={}
 *     map={} />
 *
 * ```
 */
export const OperationalLayersSettings = ({
  operationalLayers,
  onOpacityChange,
  onToggle,
  onToggleExpand,
  expandedLayer,
  onLayerVisibilityChange,
  onSublayerVisibilityChange
}: OperationalLayersSettingsProps) => {
  const classes: any = useStyles();
  const {isCurrent}  = useThemeContext();

  /**
   * @private
   */
  const changeOpacity = useCallback(updated => {
    onOpacityChange(updated);
  }, []);

  return (
    <div className={classes.root}>
      {
        operationalLayers?.map(function(layer, index) {
          const layerContent = (
            <Layer layer={layer}
              classes={classes}
              onLayerVisibilityChange={onLayerVisibilityChange}
              onToggle={onToggle}
              onToggleExpand={onToggleExpand}
              expandedLayer={expandedLayer}
              changeOpacity={changeOpacity}
              onSublayerVisibilityChange={onSublayerVisibilityChange}/>
          );
          return isCurrent ? (
            <div key={index} className={classes.currentRow}>
              {layerContent}
            </div>
          ) : (
            <React.Fragment key={index}>
              {layerContent}
            </React.Fragment>
          );
        })
      }
    </div>
  );
};

function areEqual({
  expandedLayer,
  operationalLayers,
  islayerVisibilityUpdate,
  layers
}: OperationalLayersSettingsProps, {
  expandedLayer           : nextExpandedLayer,
  operationalLayers       : nextOperationalLayers,
  islayerVisibilityUpdate : nextIslayerVisibilityUpdate,
  layers                  : nextLayers
}: OperationalLayersSettingsProps) {
  return expandedLayer === nextExpandedLayer &&
  islayerVisibilityUpdate === nextIslayerVisibilityUpdate &&
  operationalLayers === nextOperationalLayers &&
  layers === nextLayers;
}

export default memo(OperationalLayersSettings, areEqual);
